import React, { useState, useEffect } from "react";
import useClipboard from "../hooks/useClipboard";
import "./CopyButton.css";

/**
 * A reusable button component with clipboard functionality
 *
 * @param {Object} props - Component props
 * @param {string} props.text - Text to copy
 * @param {string} props.className - Additional CSS class
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.successMessage - Message to show on success
 * @param {number} props.resetAfter - Time in ms to reset status
 * @param {Function} props.onSuccess - Callback on success
 * @param {Function} props.onError - Callback on error
 */
const CopyButton = ({
  text,
  className = "",
  children = "Copy",
  successMessage = "Copied!",
  resetAfter = 2000,
  onSuccess,
  onError,
  ...props
}) => {
  const { copyToClipboard, copyStatus } = useClipboard();
  const [showModal, setShowModal] = useState(false);

  // Reset success/error message after delay
  useEffect(() => {
    let timer;
    if (copyStatus.copied || copyStatus.error) {
      timer = setTimeout(() => {
        setShowModal(false);
      }, resetAfter);
    }
    return () => clearTimeout(timer);
  }, [copyStatus, resetAfter]);

  const handleCopy = async () => {
    const success = await copyToClipboard(
      text,
      () => {
        if (onSuccess) onSuccess();
      },
      (error) => {
        if (onError) onError(error);
        setShowModal(true);
      }
    );

    if (!success) {
      setShowModal(true);
    }
  };

  return (
    <>
      <button
        className={`copy-button ${className} ${
          copyStatus.copied ? "success" : ""
        } ${copyStatus.error ? "error" : ""}`}
        onClick={handleCopy}
        {...props}
      >
        {copyStatus.copied ? successMessage : children}
      </button>

      {/* Manual copy modal */}
      {showModal && (
        <div
          className="copy-modal-backdrop"
          onClick={() => setShowModal(false)}
        >
          <div
            className="copy-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="copy-modal-close"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
            <h3>Copy Manually</h3>
            <p>Clipboard access was denied. Please copy this text manually:</p>
            <textarea
              value={text}
              onClick={(e) => e.target.select()}
              readOnly
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CopyButton;
